import {useCallback, useEffect, useRef} from 'react';

import type {SSOdMessage} from '@pexip/infinity';

import {
    type SsoAbandonReason,
    type SsoFlow,
    startSsoFlow,
} from '../services/SsoTransport.service';

/**
 * Drives a single SSO flow on whichever surface the host shell provides: a
 * same-origin popup in the browser, or the system browser plus a deep link in
 * the native shell.
 *
 * @example
 * ```ts
 * const {openSso, closeSso} = useSsoFlow({
 *     onSsoMessage: message => registration.handleSsoResult(message),
 *     onSsoAbandoned: () => registration.setStep('register'),
 * });
 * ```
 */
export const useSsoFlow = ({
    onSsoMessage,
    onSsoAbandoned,
    onSsoBlocked,
}: {
    onSsoMessage: (message: SSOdMessage) => void;
    onSsoAbandoned: (reason: SsoAbandonReason) => void;
    onSsoBlocked?: () => void;
}) => {
    const flowRef = useRef<SsoFlow | undefined>(undefined);

    const closeSso = useCallback(() => {
        flowRef.current?.close();
        flowRef.current = undefined;
    }, []);

    const openSso = useCallback(
        (redirectUrl: string, disablePopupFlow?: boolean) => {
            closeSso();

            const flow = startSsoFlow({
                redirectUrl,
                preferCurrentView: Boolean(disablePopupFlow),
                onResult: message => {
                    flowRef.current = undefined;
                    onSsoMessage(message);
                },
                onAbandon: reason => {
                    flowRef.current = undefined;
                    onSsoAbandoned(reason);
                },
            });

            if (!flow) {
                onSsoBlocked?.();
                return;
            }
            flowRef.current = flow;
        },
        [closeSso, onSsoMessage, onSsoAbandoned, onSsoBlocked],
    );

    const redirectSso = useCallback((redirectUrl: string) => {
        flowRef.current?.redirect(redirectUrl);
    }, []);

    const isSsoOpen = useCallback(() => flowRef.current !== undefined, []);

    useEffect(
        () => () => {
            flowRef.current?.close();
            flowRef.current = undefined;
        },
        [],
    );

    return {openSso, closeSso, redirectSso, isSsoOpen};
};
